import { omit } from 'lodash'
import type { Schema, FieldSpec } from '@wix/wix-data-schema-types'
import {
  FieldType,
  MULTILINGUAL_PLUGIN_ID,
  CMS_FIELD_PLUGIN_ID,
} from '@wix/wix-data-schema-types'

import type { ImplicitPluginFixes } from '../types'

export const TRANSLATABLE_FIELD_TYPES = [
  FieldType.url,
  FieldType.text,
  FieldType.richText,
  FieldType.richContent,
  FieldType.image,
]

export const EXCLUDE_PREDICATES: Array<
  (field: FieldSpec) => boolean | undefined
> = [
  ({ isDeleted }) => isDeleted,
  ({ systemField }) => systemField,
  ({ pii }) => pii,
  ({ readOnly }) => readOnly,
  ({ plugins }) => plugins?.[CMS_FIELD_PLUGIN_ID]?.internal,
]

const getTranslatableFieldKeys = (
  schema: Schema,
  translatableFieldTypes: FieldType[],
): string[] => {
  return Object.entries(schema.fields)
    .filter(
      ([_, field]) =>
        !EXCLUDE_PREDICATES.some(fieldPredicate => fieldPredicate(field)),
    )
    .filter(([_, { type }]) => translatableFieldTypes.includes(type))
    .map(([fieldKey]) => fieldKey)
}

const multilingualPluginDisabled = (schema: Schema): boolean =>
  !schema.plugins?.[MULTILINGUAL_PLUGIN_ID]

const removeMultilingualPlugin = (schema: Schema): Schema => ({
  ...schema,
  plugins: schema.plugins && omit(schema.plugins, MULTILINGUAL_PLUGIN_ID),
})

const updateMultilingualPlugin = (
  schema: Schema,
  translatableFieldKeys: string[],
): Schema => ({
  ...schema,
  plugins: {
    ...schema.plugins,
    [MULTILINGUAL_PLUGIN_ID]: {
      translatable: translatableFieldKeys,
    },
  },
})

const syncMultilingualPluginWithSchemaFields = (schema: Schema): Schema => {
  const translatableFieldKeys = getTranslatableFieldKeys(
    schema,
    TRANSLATABLE_FIELD_TYPES,
  )

  if (translatableFieldKeys.length === 0) {
    return removeMultilingualPlugin(schema)
  }

  return updateMultilingualPlugin(schema, translatableFieldKeys)
}

export const applyImplicitMultilingualPluginFixes = ({
  schema,
}: ImplicitPluginFixes): ImplicitPluginFixes => ({
  schema: multilingualPluginDisabled(schema)
    ? schema
    : syncMultilingualPluginWithSchemaFields(schema),
})
